import React from 'react';

// Gemini TTS Voice Options
const GEMINI_VOICES = [
    { id: 'Kore', name: 'Kore', description: 'Firm' },
    { id: 'Puck', name: 'Puck', description: 'Upbeat' },
    { id: 'Charon', name: 'Charon', description: 'Informative' },
    { id: 'Zephyr', name: 'Zephyr', description: 'Bright' },
    { id: 'Fenrir', name: 'Fenrir', description: 'Excitable' },
    { id: 'Leda', name: 'Leda', description: 'Youthful' },
    { id: 'Orus', name: 'Orus', description: 'Firm' },
    { id: 'Aoede', name: 'Aoede', description: 'Breezy' },
    { id: 'Callirrhoe', name: 'Callirrhoe', description: 'Easy-going' },
    { id: 'Autonoe', name: 'Autonoe', description: 'Bright' },
    { id: 'Enceladus', name: 'Enceladus', description: 'Breathy' },
    { id: 'Iapetus', name: 'Iapetus', description: 'Clear' },
    { id: 'Umbriel', name: 'Umbriel', description: 'Easy-going' },
    { id: 'Algieba', name: 'Algieba', description: 'Smooth' },
    { id: 'Despina', name: 'Despina', description: 'Smooth' },
    { id: 'Erinome', name: 'Erinome', description: 'Clear' },
    { id: 'Algenib', name: 'Algenib', description: 'Gravelly' },
    { id: 'Rasalgethi', name: 'Rasalgethi', description: 'Informative' },
    { id: 'Laomedeia', name: 'Laomedeia', description: 'Upbeat' },
    { id: 'Achernar', name: 'Achernar', description: 'Soft' },
    { id: 'Alnilam', name: 'Alnilam', description: 'Firm' },
    { id: 'Schedar', name: 'Schedar', description: 'Even' },
    { id: 'Gacrux', name: 'Gacrux', description: 'Mature' },
    { id: 'Pulcherrima', name: 'Pulcherrima', description: 'Forward' },
    { id: 'Achird', name: 'Achird', description: 'Friendly' },
    { id: 'Zubenelgenubi', name: 'Zubenelgenubi', description: 'Casual' },
    { id: 'Vindemiatrix', name: 'Vindemiatrix', description: 'Gentle' },
    { id: 'Sadachbia', name: 'Sadachbia', description: 'Lively' },
    { id: 'Sadaltager', name: 'Sadaltager', description: 'Knowledgeable' },
    { id: 'Sulafat', name: 'Sulafat', description: 'Warm' }
];

// TTS Style Options
const TTS_STYLES = [
    { id: 'normal', name: 'Normal' },
    { id: 'cheerful', name: 'Cheerful' },
    { id: 'calm', name: 'Calm' },
    { id: 'excited', name: 'Excited' },
    { id: 'professional', name: 'Professional' },
    { id: 'whisper', name: 'Whisper' },
    { id: 'dramatic', name: 'Dramatic' },
    { id: 'friendly', name: 'Friendly' }
];

// TTS Pace Options
const TTS_PACE = [
    { id: 'slow', name: 'Slow' },
    { id: 'normal', name: 'Normal' },
    { id: 'fast', name: 'Fast' }
];

const TTSSettingsPanel = ({ 
    showTTSSettings, 
    ttsSettings, 
    onSettingsChange 
}) => {
    if (!showTTSSettings) return null;

    const handleSettingChange = (key, value) => {
        onSettingsChange(prev => ({ ...prev, [key]: value }));
    };

    return (
        <div className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4">
            <div className="max-w-4xl mx-auto">
                <h3 className="text-lg font-rasa font-semibold mb-4 text-gray-800 dark:text-gray-200 font-dm-serif">
                    Voice Settings
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {/* Enable/Disable TTS */}
                    <div>
                        <label className="flex items-center space-x-2">
                            <input
                                type="checkbox"
                                checked={ttsSettings.enabled}
                                onChange={(e) => handleSettingChange('enabled', e.target.checked)}
                                className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                            />
                            <span className="font-rasa font-medium text-gray-700 dark:text-gray-300">
                                Enable Voice
                            </span>
                        </label>
                    </div>
                    
                    {/* Voice Selection */}
                    <div>
                        <label className="block font-rasa font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Voice
                        </label>
                        <select
                            value={ttsSettings.voice}
                            onChange={(e) => handleSettingChange('voice', e.target.value)}
                            className="w-full font-rasa px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                            disabled={!ttsSettings.enabled}
                        >
                            {GEMINI_VOICES.map(voice => (
                                <option key={voice.id} value={voice.id}>
                                    {voice.name} ({voice.description})
                                </option>
                            ))}
                        </select>
                    </div>
                    
                    {/* Style Selection */}
                    <div>
                        <label className="block font-rasa font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Style
                        </label>
                        <select
                            value={ttsSettings.style}
                            onChange={(e) => handleSettingChange('style', e.target.value)}
                            className="w-full font-rasa px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                            disabled={!ttsSettings.enabled}
                        >
                            {TTS_STYLES.map(style => (
                                <option key={style.id} value={style.id}>
                                    {style.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    
                    {/* Pace Selection */}
                    <div>
                        <label className="block font-rasa font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Pace
                        </label>
                        <select
                            value={ttsSettings.pace}
                            onChange={(e) => handleSettingChange('pace', e.target.value)}
                            className="w-full font-rasa px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                            disabled={!ttsSettings.enabled}
                        >
                            {TTS_PACE.map(pace => (
                                <option key={pace.id} value={pace.id}>
                                    {pace.name}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
                
                {/* Current Settings Display */}
                {ttsSettings.enabled && (
                    <div className="mt-3 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                        <p className="font-rasa text-sm text-purple-700 dark:text-purple-300">
                            <strong>Current voice:</strong> {GEMINI_VOICES.find(v => v.id === ttsSettings.voice)?.name} - {GEMINI_VOICES.find(v => v.id === ttsSettings.voice)?.description} | 
                            <strong> Style:</strong> {TTS_STYLES.find(s => s.id === ttsSettings.style)?.name} | 
                            <strong> Pace:</strong> {TTS_PACE.find(p => p.id === ttsSettings.pace)?.name}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TTSSettingsPanel;